import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Cookie consent banner built on Shopify's Customer Privacy API.
 *
 * Replaces the platform's stock privacy banner (turn that off under
 * Settings → Customer privacy → Cookie banner, or both will show). The card
 * opens as a non-modal dialog when the API says a decision is still needed
 * for the visitor's region, and every choice is written back with
 * `setTrackingConsent`, so Shopify pixels, checkout and apps honour it the
 * same way they honour the stock banner.
 *
 * - "Accept all" grants every category; "Decline" and the close button keep
 *   only essential cookies on a first visit.
 * - "Manage preferences" flips the card to per-category switches. The
 *   "sale of data" switch is only shown where the CCPA applies.
 * - Any `[data-cookie-preferences]` element on the page (the footer link)
 *   reopens the preferences view later; those triggers stay hidden until
 *   the API has loaded, so they never appear where consent is unavailable.
 * - `?cookie_banner=1` forces the card open for previewing copy and colours.
 * - Shopify's stock privacy banner keeps rendering until it is switched off
 *   in admin; while this component is on the page it is removed as soon as
 *   it appears, so visitors never see two banners.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} intro
 * @property {HTMLFieldSetElement} preferences
 * @property {HTMLElement} [preferencesHeading]
 * @property {HTMLElement} introActions
 * @property {HTMLElement} preferenceActions
 * @property {HTMLInputElement[]} [switches]
 *
 * @typedef {'intro' | 'preferences'} View
 * @typedef {{ analytics: boolean, marketing: boolean, preferences: boolean, sale_of_data: boolean }} Consent
 * @typedef {'yes' | 'no' | ''} ConsentValue
 * @typedef {object} CustomerPrivacy
 * @property {() => boolean} shouldShowBanner
 * @property {() => string} [getRegulation]
 * @property {() => Record<keyof Consent, ConsentValue>} [currentVisitorConsent]
 * @property {(consent: Consent, callback: (error?: unknown) => void) => void} setTrackingConsent
 *
 * @extends Component<Refs>
 */
class CookieBannerComponent extends Component {
  requiredRefs = ['dialog', 'intro', 'preferences', 'introActions', 'preferenceActions'];

  static categories = /** @type {const} */ (['analytics', 'marketing', 'preferences', 'sale_of_data']);

  /** How long a consent write may take before the card closes anyway. */
  static saveTimeout = 4000;

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** @type {Promise<CustomerPrivacy | null> | undefined} */
  #api;

  /** @type {HTMLElement | null} */
  #returnFocus = null;

  /** True while the card is asking for the visitor's first decision. */
  #collecting = false;

  /** @type {MutationObserver | undefined} */
  #stockBannerObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#suppressStockBanner();
    document.addEventListener('click', this.#handleTrigger, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    if (this.dataset.designMode === 'true') {
      document.addEventListener('shopify:section:select', this.#handleSectionSelect, { signal });
      document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect, { signal });
      return;
    }

    this.#api = this.#loadApi();
    this.#api.then((api) => {
      if (!this.isConnected) return;
      if (api) this.#revealTriggers();

      const forced = new URLSearchParams(location.search).get('cookie_banner') === '1';
      if (forced || api?.shouldShowBanner()) {
        this.#collecting = true;
        this.#schedule(Number(this.dataset.delay || 0) * 1000);
      }
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.#stockBannerObserver?.disconnect();
    super.disconnectedCallback();
  }

  /**
   * Removes Shopify's stock privacy banner now and whenever its script adds
   * it later; the preferences modal it opens is left alone since nothing can
   * open it once the banner is gone.
   */
  #suppressStockBanner() {
    const remove = () => {
      for (const banner of document.querySelectorAll('#shopify-pc__banner')) banner.remove();
    };
    remove();
    this.#stockBannerObserver = new MutationObserver(remove);
    this.#stockBannerObserver.observe(document.body, { childList: true });
  }

  /**
   * Opens the card, or switches its view when it is already open.
   *
   * @param {View} [view]
   */
  async show(view = 'intro') {
    clearTimeout(this.#timer);
    if (!this.isConnected) return;

    const api = await this.#api;
    this.#syncRegulation(api);
    this.#syncSwitches(api);
    this.#setView(view);

    const { dialog } = this.refs;
    if (dialog.open) return;

    // A non-modal dialog still moves focus to its first control when shown.
    // Leave the visitor where they were; callers that open the card on
    // purpose (the footer link, the "manage" button) place focus themselves.
    const previous = document.activeElement;
    dialog.show();
    if (previous instanceof HTMLElement && previous !== document.body) {
      previous.focus();
    } else if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  /**
   * Closes the card and hands focus back to whatever opened it.
   */
  async hide() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#setView('intro');

    const target = this.#returnFocus;
    this.#returnFocus = null;
    if (target?.isConnected) target.focus();
  }

  acceptAll() {
    this.#setConsent({ analytics: true, marketing: true, preferences: true, sale_of_data: true });
  }

  decline() {
    this.#setConsent({ analytics: false, marketing: false, preferences: false, sale_of_data: false });
  }

  savePreferences() {
    const consent = /** @type {Consent} */ ({});
    for (const category of CookieBannerComponent.categories) {
      consent[category] = this.#switch(category)?.checked ?? false;
    }
    this.#setConsent(consent);
  }

  openPreferences() {
    this.#setView('preferences');
    this.refs.preferencesHeading?.focus();
  }

  closePreferences() {
    this.#setView('intro');
    this.refs.intro.querySelector('h2')?.focus();
  }

  /**
   * The close button and Escape. A first-visit request treats this as
   * declining, so nothing is granted silently; a reopened preferences view
   * simply closes.
   */
  dismiss() {
    if (this.#collecting) {
      this.decline();
      return;
    }
    this.hide();
  }

  /**
   * @param {Consent} consent
   */
  async #setConsent(consent) {
    this.#setBusy(true);

    const api = await this.#api;
    if (api) {
      // The API reports a failed write through its callback (for example on
      // a local dev proxy, where the consent request cannot reach the shop).
      // The card closes either way; Shopify asks again on the next visit if
      // nothing was saved.
      await Promise.race([
        new Promise((resolve) => api.setTrackingConsent(consent, (error) => resolve(error))),
        new Promise((resolve) => setTimeout(resolve, CookieBannerComponent.saveTimeout)),
      ]);
    }

    this.#collecting = false;
    this.#setBusy(false);
    document.dispatchEvent(new CustomEvent('cookie-banner:consent', { detail: consent }));
    this.hide();
  }

  /**
   * @returns {Promise<CustomerPrivacy | null>}
   */
  #loadApi() {
    return new Promise((resolve) => {
      const shopify = /** @type {any} */ (window).Shopify;
      if (shopify?.customerPrivacy?.setTrackingConsent) {
        resolve(shopify.customerPrivacy);
        return;
      }
      if (typeof shopify?.loadFeatures !== 'function') {
        resolve(null);
        return;
      }
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (/** @type {unknown} */ error) => {
        resolve(error ? null : (shopify.customerPrivacy ?? null));
      });
    });
  }

  /**
   * @param {number} delay
   */
  #schedule(delay) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.show('intro'), delay);
  }

  /**
   * @param {View} view
   */
  #setView(view) {
    const { intro, preferences, introActions, preferenceActions } = this.refs;
    const showPreferences = view === 'preferences';
    intro.hidden = showPreferences;
    introActions.hidden = showPreferences;
    preferences.hidden = !showPreferences;
    preferenceActions.hidden = !showPreferences;
    this.dataset.view = view;
    this.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', String(showPreferences));
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    for (const button of this.querySelectorAll('button')) {
      button.disabled = busy;
    }
    this.refs.dialog.setAttribute('aria-busy', String(busy));
  }

  /**
   * Shows the "sale of data" switch only where the CCPA applies.
   *
   * @param {CustomerPrivacy | null} api
   */
  #syncRegulation(api) {
    const regulation = api?.getRegulation?.() ?? '';
    this.dataset.regulation = regulation;
    for (const row of this.querySelectorAll('[data-regulation]')) {
      if (row instanceof HTMLElement) row.hidden = row.dataset.regulation !== regulation;
    }
  }

  /**
   * Pre-fills the switches from the visitor's current consent. Where nothing
   * has been decided, opt-in regimes (GDPR) start off and opt-out regimes
   * start on, matching how Shopify treats the undecided state.
   *
   * @param {CustomerPrivacy | null} api
   */
  #syncSwitches(api) {
    const current = api?.currentVisitorConsent?.();
    const optIn = this.dataset.regulation === 'GDPR';
    for (const category of CookieBannerComponent.categories) {
      const input = this.#switch(category);
      if (!input) continue;
      const value = current?.[category] ?? '';
      input.checked = value === 'yes' || (value === '' && !optIn);
    }
  }

  /**
   * @param {keyof Consent} category
   */
  #switch(category) {
    return this.refs.switches?.find((input) => input.name === category) ?? null;
  }

  #revealTriggers() {
    for (const trigger of document.querySelectorAll('[data-cookie-preferences]')) {
      if (trigger instanceof HTMLElement) trigger.hidden = false;
    }
  }

  /** @param {Event} event */
  #handleTrigger = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-cookie-preferences]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    this.#returnFocus = trigger;
    this.show('preferences').then(() => this.refs.preferencesHeading?.focus());
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.refs.dialog.open) return;
    event.preventDefault();
    this.dismiss();
  };

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    if (event instanceof CustomEvent && event.detail?.sectionId === this.dataset.sectionId) this.show('intro');
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    if (event instanceof CustomEvent && event.detail?.sectionId === this.dataset.sectionId) this.hide();
  };
}

if (!customElements.get('cookie-banner-component')) {
  customElements.define('cookie-banner-component', CookieBannerComponent);
}
